/**
 * The Text renderer of the training: plain text with three inline marks only.
 *   **bold**        -> <strong>
 *   `Screen label`  -> a UI chip (the exact words on the screen)
 *   {key:c}         -> <Kbd>
 * Nothing else is interpreted: no HTML, no links.
 */

import { Kbd } from "@norican/react";
import { Fragment, type ReactNode } from "react";

import { FIGURES } from "@/components/figures";

import type { Block, Text as TextValue } from "../types";

const TOKEN = /(\*\*[^*]+\*\*|`[^`]+`|\{key:[^}]+\})/g;

/** The inline marks of one text as React nodes. */
export function renderText(text: TextValue): ReactNode[] {
  return text.split(TOKEN).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <span
          key={index}
          className="border-border bg-surface text-fg mx-0.5 inline-block rounded border px-1.5 py-px text-[0.92em] font-semibold whitespace-normal"
          data-ui-label
        >
          {part.slice(1, -1)}
        </span>
      );
    }
    if (part.startsWith("{key:") && part.endsWith("}")) {
      return (
        <Kbd key={index} size="sm">
          {part.slice(5, -1)}
        </Kbd>
      );
    }
    return <Fragment key={index}>{part}</Fragment>;
  });
}

/** A text with its marks. */
export function Text({ value }: { value: TextValue }) {
  return <>{renderText(value)}</>;
}

/** Plain words of a text (marks removed), for aria labels and titles. */
export function plainText(text: TextValue): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\{key:([^}]+)\}/g, "$1");
}

/** The content blocks of a slide. */
export function Blocks({ blocks }: { blocks: readonly Block[] }) {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {blocks.map((block, index) => (
        <BlockView key={index} block={block} />
      ))}
    </div>
  );
}

function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case "text":
      return (
        <p className="text-fg text-base leading-relaxed break-words">
          <Text value={block.text} />
        </p>
      );
    case "list":
      return (
        <ul className="text-fg flex list-disc flex-col gap-1.5 pl-5 text-base leading-relaxed">
          {block.items.map((item, index) => (
            <li key={index} className="break-words">
              <Text value={item} />
            </li>
          ))}
        </ul>
      );
    case "steps":
      return (
        <ol className="text-fg flex list-decimal flex-col gap-1.5 pl-5 text-base leading-relaxed">
          {block.steps.map((step, index) => (
            <li key={index} className="break-words">
              <Text value={step} />
            </li>
          ))}
        </ol>
      );
    case "note":
      return (
        <div
          className={
            block.tone === "warning"
              ? "border-warning-border bg-warning-subtle rounded-md border p-3"
              : "border-info-border bg-info-subtle rounded-md border p-3"
          }
          role="note"
        >
          <p className="text-fg text-sm font-semibold break-words">
            <Text value={block.title} />
          </p>
          <p className="text-fg mt-1 text-sm leading-relaxed break-words">
            <Text value={block.text} />
          </p>
        </div>
      );
    case "keys":
      return (
        <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-2">
          {block.keys.map((entry) => (
            <Fragment key={entry.key}>
              <dt>
                <Kbd size="sm">{entry.key}</Kbd>
              </dt>
              <dd className="text-fg text-sm break-words">
                <Text value={entry.does} />
              </dd>
            </Fragment>
          ))}
        </dl>
      );
    case "figure": {
      // The caption is plain words: the figure frame prints it as one muted line.
      const Figure = FIGURES[block.figure];
      return <Figure caption={block.caption ? plainText(block.caption) : undefined} />;
    }
    default:
      return null;
  }
}
